import { useRef } from "react";
import { motion, type Transition } from "motion/react";
import { Award, Sparkles } from "lucide-react";
import logoImage from "figma:asset/logo.png";
import { Button } from "@primitive/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@primitive/dialog";
import type { AchievementUnlockedCelebration } from "./celebration.types";

type AchievementCelebrationProps = {
  celebration: AchievementUnlockedCelebration;
  onDismiss: () => void;
  shouldReduceMotion: boolean;
};

const itemInitial = { opacity: 0, y: 8, filter: "blur(3px)" };
const itemAnimate = { opacity: 1, y: 0, filter: "blur(0px)" };
const softEase = [0.22, 1, 0.36, 1] as const;

function getTransition(
  index: number,
  shouldReduceMotion: boolean,
): Transition {
  if (shouldReduceMotion) return { duration: 0.01 };
  return { duration: 0.24, ease: softEase, delay: index * 0.07 };
}

export function AchievementCelebration({
  celebration,
  onDismiss,
  shouldReduceMotion,
}: AchievementCelebrationProps) {
  const actionRef = useRef<HTMLButtonElement | null>(null);
  const message =
    celebration.message ??
    "Your progress has been recorded and added to your achievement history.";

  return (
    <Dialog open onOpenChange={(open) => !open && onDismiss()}>
      <DialogContent
        className="overflow-hidden border-border/80 bg-card p-0 shadow-2xl sm:max-w-md"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          actionRef.current?.focus();
        }}
      >
        <div className="relative px-6 pb-6 pt-7 text-center">
          <div className="absolute inset-x-0 top-0 h-1 bg-linear-to-r from-emerald-500 via-amber-400 to-blue-500" />
          <DialogHeader className="items-center text-center">
            <motion.div
              className="mb-1 inline-flex items-center gap-2 rounded-full border border-border/70 bg-muted/50 px-3 py-1 text-xs font-medium text-muted-foreground"
              initial={shouldReduceMotion ? { opacity: 0 } : itemInitial}
              animate={itemAnimate}
              transition={getTransition(1, shouldReduceMotion)}
            >
              <Sparkles className="size-3.5 text-amber-500" />
              Achievement unlocked
            </motion.div>
            <motion.div
              className="flex size-20 items-center justify-center rounded-2xl border border-border bg-background p-3 shadow-lg shadow-black/10"
              initial={
                shouldReduceMotion
                  ? { opacity: 0 }
                  : { opacity: 0, y: 10, scale: 0.92 }
              }
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={getTransition(2, shouldReduceMotion)}
            >
              {celebration.asset === "bbb-logo" || !celebration.asset ? (
                <img src={logoImage} alt="" className="size-full object-contain" />
              ) : (
                <Award className="size-9 text-amber-500" />
              )}
            </motion.div>
            <DialogTitle asChild>
              <motion.h2
                className="text-xl font-semibold leading-tight text-foreground"
                initial={shouldReduceMotion ? { opacity: 0 } : itemInitial}
                animate={itemAnimate}
                transition={getTransition(3, shouldReduceMotion)}
              >
                {celebration.title}
              </motion.h2>
            </DialogTitle>
            <DialogDescription asChild>
              <motion.p
                className="max-w-sm text-sm leading-6 text-muted-foreground"
                initial={shouldReduceMotion ? { opacity: 0 } : itemInitial}
                animate={itemAnimate}
                transition={getTransition(4, shouldReduceMotion)}
              >
                {message}
              </motion.p>
            </DialogDescription>
          </DialogHeader>
          {celebration.metadata?.length ? (
            <motion.dl
              className="mt-5 grid gap-2 rounded-lg border border-border/70 bg-muted/35 p-3 text-left"
              initial={shouldReduceMotion ? { opacity: 0 } : itemInitial}
              animate={itemAnimate}
              transition={getTransition(5, shouldReduceMotion)}
            >
              {celebration.metadata.map((item) => (
                <div key={`${item.label}:${item.value}`} className="flex items-center justify-between gap-4 text-xs">
                  <dt className="text-muted-foreground">{item.label}</dt>
                  <dd className="font-medium text-foreground">{item.value}</dd>
                </div>
              ))}
            </motion.dl>
          ) : null}
          <motion.div
            className="mt-6 flex justify-center"
            initial={shouldReduceMotion ? { opacity: 0 } : itemInitial}
            animate={itemAnimate}
            transition={getTransition(6, shouldReduceMotion)}
          >
            <Button ref={actionRef} onClick={onDismiss}>
              {celebration.actionLabel ?? "Continue"}
            </Button>
          </motion.div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
